import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Check } from 'lucide-react';
import { getSignupUrl } from '@/lib/appLinks';
import { trackSignupIntent } from '@/lib/tracking';

interface Plan {
    id: string;
    name: string;
    description: string;
    monthlyPrice: number;
    annualPrice: number;
    features: string[];
    cta: string;
    popular: boolean;
    highlight: boolean;
}

interface PricingCardProps {
    plan: Plan;
    isAnnual: boolean;
}

export const PricingCard: React.FC<PricingCardProps> = ({ plan, isAnnual }) => {
  return (
    <div className={`relative group ${plan.popular ? 'md:-mt-4 z-10' : ''}`}>
        {plan.popular && (
        <div className="absolute -top-4 left-0 right-0 flex justify-center z-20">
            <Badge className="bg-primary text-primary-foreground px-3 py-1 shadow-lg text-[10px] font-bold uppercase tracking-wider rounded-full">
            Best Value
            </Badge>
        </div>
        )}

        <Card className={`h-full flex flex-col border transition-all duration-300 hover:shadow-xl
        ${plan.highlight 
            ? 'border-primary shadow-lg shadow-primary/5 bg-card scale-105 md:scale-100 md:hover:scale-105' 
            : 'border-border bg-card/50 hover:bg-card hover:border-primary/30'
        }
        `}>
        <CardHeader className="p-6 pb-4">
            <CardTitle className="text-xl font-bold">{plan.name}</CardTitle>
            <p className="text-xs text-muted-foreground mt-2 min-h-[32px]">{plan.description}</p>
        </CardHeader>
        
        <CardContent className="p-6 pt-0 flex-grow">
            <div className="mb-6 flex items-baseline">
            <span className="text-3xl font-extrabold tracking-tight">
                ${isAnnual ? Math.round(plan.annualPrice / 12) : plan.monthlyPrice}
            </span>
            <span className="text-muted-foreground ml-1 text-xs font-medium">/mo</span>
            </div>
            {isAnnual && plan.annualPrice > 0 && (
            <p className="text-[10px] text-muted-foreground -mt-5 mb-4">
                Billed ${plan.annualPrice} yearly
            </p>
            )}

            <Button 
            size="default" 
            className={`w-full font-semibold rounded-full h-10 ${plan.highlight ? 'shadow-md shadow-primary/20' : ''}`} 
            variant={plan.highlight ? 'default' : 'outline'}
            asChild
            >
            <a href={getSignupUrl(plan.id)} onClick={() => trackSignupIntent('pricing_card', plan.id)}>{plan.cta}</a>
            </Button>

            <div className="mt-6 space-y-3">
            <p className="text-[10px] font-bold uppercase text-muted-foreground tracking-wider">Includes:</p>
            <ul className="space-y-2">
                {plan.features.map((feature, i) => (
                <li key={i} className="flex items-start gap-2 text-xs text-foreground/90">
                    <Check size={14} className={`mt-0.5 shrink-0 ${plan.highlight ? 'text-primary' : 'text-muted-foreground'}`} strokeWidth={2.5} />
                    <span className="leading-tight">{feature}</span>
                </li>
                ))}
            </ul>
            </div>
        </CardContent>
        </Card>
    </div>
  );
};
